import { Button, Col, Form, Input, message, Row, Switch } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../components/spinner";
import { getApiErrorMsg, getApiResponseMsg } from "../../../utils/object-util";
import styles from "./form.module.scss";

const { Item } = Form;

interface IRedirectDetailsProps {
	mode: "create" | "edit" | null;
	id: string | null;
	setActiveKey: (arg: string) => void;
}

const RedirectDetails = ({ mode, id, setActiveKey }: IRedirectDetailsProps) => {
	const isEditing = mode === "edit" && id !== null;
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [loading, setLoading] = useState(isEditing);
	const [isActiveValue, setIsActiveValue] = useState(false);

	useEffect(() => {
		if (isEditing) {
			setLoading(true);
			axios
				.get(`events/${id}/redirect`)
				.then((response) => {
					const { data } = response.data;
					form.setFieldsValue(data);
					setIsActiveValue(data.isActive || false);
				})
				.catch((error) => {
					message.error({ content: getApiErrorMsg(error), key: "error", duration: 2 });
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [mode, id]);

	const handleSubmit = (data: any) => {
		setIsSubmitting(true);
		const trimmedUrl = data.url?.trim();

		const payload = { isActive: data.isActive ?? true, url: trimmedUrl && trimmedUrl !== "" ? trimmedUrl : null };

		axios
			.put(`events/${id}/redirect`, payload)
			.then((res) => {
				if (res?.data?.data) {
					message.success({ content: getApiResponseMsg(res), key: "success", duration: 2 });
				}
			})
			.catch((error) => {
				message.error({ content: getApiErrorMsg(error), key: "error", duration: 2 });
			})
			.finally(() => {
				if (mode === "create") {
					navigate(`/events/event-types`);
				} else {
					setActiveKey("0");
				}
				setIsSubmitting(false);
			});
	};

	if (loading) return <Spinner />;

	return (
		<>
			<Row>
				<Col sm={12}>
					<Form onFinish={handleSubmit} form={form} layout="vertical" initialValues={{ isActive: false, eventTypes: [] }} disabled={isSubmitting}>
						<Item
							name="url"
							label="Redirect URL"
							rules={[
								{ required: isActiveValue, message: "Redirect URL is required" },
								{
									validator: (_: any, value: string) => {
										if (!isActiveValue) {
											return Promise.resolve();
										}
										if (!value) {
											return Promise.resolve();
										}

										if (!value.startsWith("https://")) {
											return Promise.reject("Redirect URL must start with https://");
										}
										// Length validation
										if (value.length > 1000) {
											return Promise.reject("URL must be less than 1000 characters");
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input placeholder="https://your-redirect-endpoint.com" />
						</Item>

						<Item name="isActive" label="Redirect Active?" valuePropName="checked">
							<Switch
								onChange={(checked) => {
									setIsActiveValue(checked);
									form.setFieldsValue({ isActive: checked });
								}}
							/>
						</Item>
					</Form>
				</Col>
			</Row>
			<div className={styles["submit-btn-cont"]}>
				<Button
					onClick={() => {
						if (mode === "edit") {
							setActiveKey("0");
						} else {
							navigate("/events/event-types");
						}
					}}
					disabled={isSubmitting}
				>
					Cancel
				</Button>
				<Button onClick={form.submit} type="primary" loading={isSubmitting}>
					{mode === "edit" ? "Save and update" : "Next"}
				</Button>
			</div>
		</>
	);
};

export default RedirectDetails;
